export const MAX_MEDIA_PER_POST = 10;
export const MAX_VIDEOS_PER_POST = 2;
export const MAX_VIDEO_BYTES = 60 * 1024 * 1024;
export const MAX_VIDEO_SECONDS = 60;
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

export const ACCEPTED_MEDIA_TYPES = 'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm';

/** Photos and videos share one list of keys; the key's extension (set by the server from the content type) says which is which. */
export const isVideoKey = (key: string): boolean => /\.(mp4|mov|webm)$/i.test(key);

export const isVideoFile = (file: File): boolean => file.type.startsWith('video/');

/** Reads a video's duration in seconds without uploading it; rejects if the browser can't decode it. */
export const getVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(video.duration); };
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that video')); };
    video.src = url;
  });

/** Returns a user-facing reason the file can't be attached, or null if it's fine. */
export const validateMediaFile = async (file: File): Promise<string | null> => {
  if (isVideoFile(file)) {
    if (file.size > MAX_VIDEO_BYTES) return 'Videos must be under 60 MB';
    try {
      const seconds = await getVideoDuration(file);
      if (seconds > MAX_VIDEO_SECONDS + 0.5) return 'Videos must be 60 seconds or shorter';
    } catch {
      return 'That video format could not be read — try MP4';
    }
    return null;
  }
  if (!file.type.startsWith('image/')) return 'Only photos and videos can be attached';
  if (file.size > MAX_IMAGE_BYTES) return 'Photos must be under 15 MB';
  return null;
};

// The API starts a transcode when a post or story with a video is created, writing these
// siblings next to the original (VideoDerivatives in VideoTranscodeService.cs — keep in sync).
// They may not exist yet, or ever (older videos), so players list them *before* the original.
const videoBase = (key: string): string => key.replace(/\.[^./]+$/, '');
export const videoWebKey = (key: string): string => `${videoBase(key)}_web.mp4`;
export const videoPosterKey = (key: string): string => `${videoBase(key)}_poster.0000000.jpg`;

export const videoMimeType = (key: string): string => {
  const ext = key.slice(key.lastIndexOf('.') + 1).toLowerCase();
  return ext === 'mov' ? 'video/quicktime' : ext === 'webm' ? 'video/webm' : 'video/mp4';
};
